import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import {
  createMcpAccessProfileApi,
  CreateMcpAccessProfileRequest,
  deleteMcpAccessProfileApi,
  getMcpAccessProfilesApi,
  McpAccessProfile,
  McpAccessProfileWithToken,
  rotateMcpAccessProfileApi
} from '../Api/McpAccess';

export interface UseMcpAccessReturn {
  profiles: McpAccessProfile[];
  loading: boolean;
  listError: string | null;
  submittingCreate: boolean;
  submittingRotateId: string | null;
  submittingRevokeId: string | null;
  loadProfiles: () => Promise<void>;
  createProfile: (req: CreateMcpAccessProfileRequest) => Promise<McpAccessProfileWithToken | null>;
  rotateProfile: (accessId: string) => Promise<McpAccessProfileWithToken | null>;
  revokeProfile: (accessId: string) => Promise<boolean>;
}

const profilesSubject = new BehaviorSubject<McpAccessProfile[]>([]);
const listLoadingSubject = new BehaviorSubject<boolean>(false);
const listErrorSubject = new BehaviorSubject<string | null>(null);
const submittingCreateSubject = new BehaviorSubject<boolean>(false);
const submittingRotateIdSubject = new BehaviorSubject<string | null>(null);
const submittingRevokeIdSubject = new BehaviorSubject<string | null>(null);

export const getMcpAccessProfilesSubject = (): BehaviorSubject<McpAccessProfile[]> => profilesSubject;
export const getMcpAccessLoadingSubject = (): BehaviorSubject<boolean> => listLoadingSubject;
export const getMcpAccessListErrorSubject = (): BehaviorSubject<string | null> => listErrorSubject;
export const getMcpAccessSubmittingCreateSubject = (): BehaviorSubject<boolean> => submittingCreateSubject;
export const getMcpAccessSubmittingRotateIdSubject = (): BehaviorSubject<string | null> => submittingRotateIdSubject;
export const getMcpAccessSubmittingRevokeIdSubject = (): BehaviorSubject<string | null> => submittingRevokeIdSubject;

export const getMcpAccessProfiles = (): McpAccessProfile[] => profilesSubject.getValue();
export const getMcpAccessLoading = (): boolean => listLoadingSubject.getValue();
export const getMcpAccessListError = (): string | null => listErrorSubject.getValue();
export const getMcpAccessSubmittingCreate = (): boolean => submittingCreateSubject.getValue();
export const getMcpAccessSubmittingRotateId = (): string | null => submittingRotateIdSubject.getValue();
export const getMcpAccessSubmittingRevokeId = (): string | null => submittingRevokeIdSubject.getValue();

export const loadMcpAccessProfiles = async (): Promise<void> => {
  listLoadingSubject.next(true);
  listErrorSubject.next(null);
  const result = await getMcpAccessProfilesApi();
  listLoadingSubject.next(false);
  if (result.error) {
    listErrorSubject.next(result.error.message || 'Could not load MCP access profiles');
    profilesSubject.next([]);
    return;
  }
  profilesSubject.next(result.data || []);
};

export const createMcpAccessProfile = async (
  req: CreateMcpAccessProfileRequest
): Promise<McpAccessProfileWithToken | null> => {
  submittingCreateSubject.next(true);
  const result = await createMcpAccessProfileApi(req);
  submittingCreateSubject.next(false);
  if (result.error || !result.data) return null;
  const created = result.data;
  profilesSubject.next([stripToken(created), ...profilesSubject.getValue()]);
  return created;
};

export const rotateMcpAccessProfile = async (accessId: string): Promise<McpAccessProfileWithToken | null> => {
  submittingRotateIdSubject.next(accessId);
  const result = await rotateMcpAccessProfileApi(accessId);
  submittingRotateIdSubject.next(null);
  if (result.error || !result.data) return null;
  const rotated = result.data;
  profilesSubject.next(profilesSubject.getValue().map((p) => (p.access_id === accessId ? stripToken(rotated) : p)));
  return rotated;
};

export const revokeMcpAccessProfile = async (accessId: string): Promise<boolean> => {
  submittingRevokeIdSubject.next(accessId);
  const result = await deleteMcpAccessProfileApi(accessId);
  submittingRevokeIdSubject.next(null);
  if (result.error) return false;
  profilesSubject.next(profilesSubject.getValue().filter((p) => p.access_id !== accessId));
  return true;
};

const stripToken = (profile: McpAccessProfileWithToken): McpAccessProfile => ({
  access_id: profile.access_id,
  title: profile.title,
  scope: profile.scope,
  last_used_at: profile.last_used_at,
  last_used_tool_name: profile.last_used_tool_name,
  last_used_client_name: profile.last_used_client_name,
  created_at: profile.created_at,
  updated_at: profile.updated_at
});

export const useMcpAccess = (): UseMcpAccessReturn => {
  const [profiles, setProfiles] = useState<McpAccessProfile[]>(getMcpAccessProfiles());
  const [loading, setLoading] = useState<boolean>(getMcpAccessLoading());
  const [listError, setListError] = useState<string | null>(getMcpAccessListError());
  const [submittingCreate, setSubmittingCreate] = useState<boolean>(getMcpAccessSubmittingCreate());
  const [submittingRotateId, setSubmittingRotateId] = useState<string | null>(getMcpAccessSubmittingRotateId());
  const [submittingRevokeId, setSubmittingRevokeId] = useState<string | null>(getMcpAccessSubmittingRevokeId());

  useEffect(() => {
    const subs = [
      profilesSubject.subscribe(setProfiles),
      listLoadingSubject.subscribe(setLoading),
      listErrorSubject.subscribe(setListError),
      submittingCreateSubject.subscribe(setSubmittingCreate),
      submittingRotateIdSubject.subscribe(setSubmittingRotateId),
      submittingRevokeIdSubject.subscribe(setSubmittingRevokeId)
    ];
    loadMcpAccessProfiles();
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  return {
    profiles,
    loading,
    listError,
    submittingCreate,
    submittingRotateId,
    submittingRevokeId,
    loadProfiles: loadMcpAccessProfiles,
    createProfile: createMcpAccessProfile,
    rotateProfile: rotateMcpAccessProfile,
    revokeProfile: revokeMcpAccessProfile
  };
};

export const resetMcpAccessStoreForTest = (): void => {
  profilesSubject.next([]);
  listLoadingSubject.next(false);
  listErrorSubject.next(null);
  submittingCreateSubject.next(false);
  submittingRotateIdSubject.next(null);
  submittingRevokeIdSubject.next(null);
};
